// Tutorial specification interface
export interface TutorialSpec {
  turnLimit: number | 'unlimited';
  isSolo?: boolean; // Whether this is a solo tutorial (no opponent moves)
  noDarkKing?: boolean; // Override: Allow tutorial to run without dark king (no auto-loss)
  noLightKing?: boolean; // Override: Allow tutorial to run without light king (no auto-loss)
  crownEnemyKings?: string[]; // Array of piece types that should show crowns when they are enemy kings

  // Victory condition overrides (Phase 2 - SOC refactoring)
  blockRaceVictory?: boolean; // Block king race wins (for tutorials focused on other win conditions)
  blockKingCapture?: boolean; // Block king capture wins
  blockKingAbsence?: boolean; // Ignore missing king auto-wins (for tutorials without kings)
  allowedVictoryConditions?: ('race' | 'capture' | 'custom')[]; // Explicitly allowed win types
  customWinConditions?: boolean; // Use TutorialWinConditions exclusively (ignore standard checks)

  // AI search depth cap for Sentinel. When set, Sentinel's iterative
  // deepening stops at this depth instead of its default (max 10). Useful
  // for early tutorials where deeper search reveals "all lines lose" and
  // makes the AI play randomly — capping at 2 keeps it focused on
  // avoiding immediate threats only, which feels more pedagogical.
  maxAIDepth?: number;
}

// Tutorial specifications including turn limits and solo mode for each tutorial
export const TUTORIAL_SPECS: { [constellation: string]: { [tutorialIndex: number]: TutorialSpec } } = {
  'Bear': {
    0: { turnLimit: 2, isSolo: true }, // Solo movement tutorial
    1: {
      turnLimit: 6,
      blockKingAbsence: true, // Prevent king-absence auto-end
      allowedVictoryConditions: ['race'] // Allow race victories
    },
    2: {
      turnLimit: 'unlimited',
      crownEnemyKings: ['Goat'],
      // Cap Sentinel at 2 ply for Predator. With deeper search Sentinel proves
      // Dark loses regardless and all moves tie at -100000 → it picks at
      // random. At depth 2 it can still see and avoid immediate king-capture
      // threats, which is the whole pedagogy of this tutorial.
      maxAIDepth: 2,
    },
    3: {
      turnLimit: 7,
      crownEnemyKings: ['Crane'],
      blockKingAbsence: true, // Ignore missing kings
      customWinConditions: true, // Use TutorialWinConditions exclusively
      allowedVictoryConditions: ['race', 'custom'] // Allow race victories for defense tutorial
    },
    4: {
      turnLimit: 'unlimited',
      allowedVictoryConditions: ['race', 'capture', 'custom'], // Race, king-capture, and tutorial-custom wins are all valid
      crownEnemyKings: ['Rhino'],
      // Cap Sentinel at 4 ply — default search (now 9) makes this tutorial
      // effectively unwinnable for a learner. 4 plays competitively without
      // calculating losing lines so deep that every option ties at -∞ and the
      // AI plays randomly (same failure mode as Bear[2]).
      maxAIDepth: 4
    }
  },
  'Crane': {
    0: {
      turnLimit: 8,
      crownEnemyKings: ['Crane'],
      allowedVictoryConditions: ['race', 'capture'], // 1v1 Crane duel — race or capture wins
      maxAIDepth: 10
    },
    1: {
      turnLimit: 8,
      blockKingAbsence: true, // Prevent king-absence auto-end for AI
      allowedVictoryConditions: ['race']
    },
    2: {
      turnLimit: 18,
      crownEnemyKings: ['Wolf'],
      blockKingAbsence: true, // Prevent king-absence auto-end for AI
      allowedVictoryConditions: ['race', 'capture']
    }
  },
  'Frog': {
    0: { turnLimit: 'unlimited' },
    1: { turnLimit: 'unlimited' },
    2: { turnLimit: 'unlimited' },
    3: { turnLimit: 'unlimited' },
    4: { turnLimit: 9 }
  },
  'Goat': {
    0: { turnLimit: 4 },
    1: { turnLimit: 'unlimited' },
    2: { turnLimit: 8 },
    3: { turnLimit: 'unlimited' }
  },
  'Monkey': {
    0: { turnLimit: 3, isSolo: true }, // Solo movement tutorial
    1: { turnLimit: 'unlimited', crownEnemyKings: ['Crane'] }, // Monkey tutorial 2: Show crown on dark king crane
    2: { turnLimit: 2 }, // Monkey gamble tutorial - only 2 moves
    3: { turnLimit: 'unlimited' },
    4: { turnLimit: 13 }
  },
  'Fungi': {
    0: { turnLimit: 5 },
    1: { turnLimit: 'unlimited' },
    2: { turnLimit: 'unlimited' },
    3: { turnLimit: 5 },
    4: { turnLimit: 'unlimited' },
    5: { turnLimit: 'unlimited' }
  },
  'Octopus': {
    0: { turnLimit: 8 },
    1: { turnLimit: 8 },
    2: { turnLimit: 7 },
    3: { turnLimit: 'unlimited' },
    4: { turnLimit: 'unlimited' },
    5: { turnLimit: 'unlimited' }
  },
  'Virus': {
    0: { turnLimit: 1 },
    1: { turnLimit: 3 },
    2: { turnLimit: 'unlimited' }
  },
  'Penguin': {
    0: { turnLimit: 'unlimited' },
    1: { turnLimit: 'unlimited' },
    2: { turnLimit: 8 },
    3: { turnLimit: 'unlimited' }
  },
  'Rhino': {
    0: { turnLimit: 7, crownEnemyKings: ['Wolf'] },
    1: { turnLimit: 'unlimited' },
    2: { turnLimit: 'unlimited' },
    3: { turnLimit: 'unlimited' },
    4: { turnLimit: 'unlimited' }
  },
  'Snake': {
    0: { turnLimit: 'unlimited' },
    1: { turnLimit: 'unlimited' },
    2: { turnLimit: 'unlimited' },
    3: { turnLimit: 'unlimited' }
  },
  'Tree': {
    0: { turnLimit: 6 },
    1: {
      turnLimit: 'unlimited',
      blockKingAbsence: true // Prevent king-absence auto-end for AI
    },
    2: {
      turnLimit: 'unlimited',
      noDarkKing: true,
      blockKingAbsence: true // noDarkKing tutorial - allow AI to play without dark king
    }
  },
  'Wolf': {
    0: { turnLimit: 7 },
    1: { turnLimit: 'unlimited' },
    2: { turnLimit: 'unlimited' },
    3: { turnLimit: 'unlimited' }
  }
};

export function getTutorialTurnLimit(constellation: string, tutorialIndex: number): number | 'unlimited' {
  const specs = TUTORIAL_SPECS[constellation];
  if (!specs || !(tutorialIndex in specs)) {
    return 'unlimited';
  }
  const spec = specs[tutorialIndex];
  return typeof spec === 'object' ? spec.turnLimit : spec;
}

export function getTutorialSpec(constellation: string, tutorialIndex: number): TutorialSpec {
  const specs = TUTORIAL_SPECS[constellation];
  if (!specs || !(tutorialIndex in specs)) {
    return { turnLimit: 'unlimited', isSolo: false };
  }
  const spec = specs[tutorialIndex];
  return typeof spec === 'object' ? spec : { turnLimit: spec, isSolo: false };
}

export function isTutorialSolo(constellation: string, tutorialIndex: number): boolean {
  const spec = getTutorialSpec(constellation, tutorialIndex);
  return spec.isSolo || false;
}

// DEPRECATED: All tutorials now use Sentinel AI
// Kept for backwards compatibility but always returns false
export function shouldTutorialUseMinimaxAI(constellation: string, tutorialIndex: number): boolean {
  return false;
}

export function tutorialHasNoDarkKingOverride(constellation: string, tutorialIndex: number): boolean {
  const spec = getTutorialSpec(constellation, tutorialIndex);
  return spec.noDarkKing || false;
}

export function tutorialHasNoLightKingOverride(constellation: string, tutorialIndex: number): boolean {
  const spec = getTutorialSpec(constellation, tutorialIndex);
  return spec.noLightKing || false;
}

/**
 * Check if a piece should show a crown in the current tutorial
 * @param constellation Tutorial constellation
 * @param tutorialIndex Tutorial index
 * @param pieceType Type of the piece to check
 * @param isKing Whether the piece is a king
 * @param isLight Whether the piece is light (player piece)
 * @returns true if the piece should show a crown
 */
export function shouldTutorialPieceShowCrown(
  constellation: string, 
  tutorialIndex: number, 
  pieceType: string, 
  isKing: boolean, 
  isLight: boolean
): boolean {
  // Only show crowns on enemy kings
  if (!isKing || isLight) {
    return false;
  }
  
  const spec = getTutorialSpec(constellation, tutorialIndex);
  if (!spec.crownEnemyKings) {
    return false;
  }
  
  return spec.crownEnemyKings.includes(pieceType);
}